/**
 * Data object for CDS Nodes.
 *
 * @typedef {Object} CDSNode
 * @prop {string} description            - the readable name of the organization.
 * @prop {string} rootId                 - the organization's CDS ID.
 * @prop {string} extensionId            - used by settings groups.
 * @prop {string} type                   - the level in the tree the customer is, which used to correspond to special icons.
 * @prop {number} numberOfAuthorsOrMtsos - the aggregate number of users an organization has.
 * @prop {Object[]} contentPackages      - list of package names and whether or not they have been modified.
 * @prop {boolean} hasStructuralChildren - a flag indicating whether a node has children.
 * @prop {CDSNode[]} groupChildren       - a list of settings group-type children.
 * @prop {CDSNode[]} children            - a list of children that are not settings groups.
 * @prop {number} distanceFromRoot       - the number of discrete nodes this node is separated from a root.
 */
export class CDSNode {
    public description: string;
    public userLoginName: string;
    public groupNames?: string[];
    public groupShortOids?: string[];
    public rootId: string;
    public extensionId: string;
    public type: string;
    public numberOfAuthorsOrMtsos: number;
    public numberOfPackages: number;
    public contentPackages: [{
      modified: boolean,
      contentPackage_id: string
      packageRules: Rule[];
    }];
    public hasStructuralChildren: boolean;
    public groupChildren: CDSNode[];
    public children: CDSNode[];
    public distanceFromRoot?: number;
    public users?: CDSNode[];
    public modified?: boolean;
}

/**
 * Data object for rule actions.
 *
 * @typedef {Object} Action
 * @prop {string} actionType        - the type of action this node is.
 * @prop {string} messageTitle      - the display title of the rule's card.
 * @prop {string} messagePriority   - a string that is either "Normal" or "Critical."
 * @prop {string} actionSubtype     - an enumeration used only by the Admin app, indicating what message components are necessary.
 * @prop {string} scriptName        - the name of a script that should trigger this message.
 * @prop {string} messageText       - the message body of a non-actionable message.
 * @prop {string} insertionLocation - an enumeration of where, if the message is actionable, the action phrase should be inserted into the document.
 * @prop {ActionGroup[]} groups     - list of pieces of the actionable message.
 */
export interface Action {
  actionType: string;
  messageTitle: string;
  messagePriority: string;
  actionSubtype?: string;
  scriptName?: string;
  messageText?: string;
  insertionLocation?: string;
  groups?: ActionGroup[];
}

/**
 * Data object for pieces of a rule action.
 *
 * @typedef {Object} ActionGroup
 * @prop {string} type        - enumeration of the type this piece is: message, radio, or dropdown.
 * @prop {string} messageText - the message text, if this is a message piece.
 * @prop {any[]} options      - the options, if this is a radio/dropdown piece.
 */
export interface ActionGroup {
  type: string;
  messageText: string;
  options: any[];
}

/**
 * Data object for CAPD rules.
 *
 * @typedef {Object} Rule
 * @prop {string} _id                   - the unique identifier for the rule.
 * @prop {string} rule_id               - the same as ID, but stored relationally.
 * @prop {string} contentPackageRule_id - the relational combination ID of the rule and its content package.
 * @prop {string} group_id              - the relational ID of the group the rule is assigned to.
 * @prop {string} org_id                - the relational ID of the organization the rule is assigned to.
 * @prop {string} contentPackage_id     - the relational ID of the package the rule is assigned to.
 * @prop {string} triggerName           - the internal name of the rule, to easily find it.
 * @prop {string} renderEnvironment     - the environment the rule is to be rendered in (CAPD, HCC, or Epic).
 * @prop {string} description           - an employee-facing description of the rule.
 * @prop {string} triggerCategory       - the category of a rule, again to find it more easily.
 * @prop {string} referenceCategory     - @todo unsure of this
 * @prop {string} triggerCode           - the numeric code that our NLP uses to identify the rule.
 * @prop {string} sqlExpression         - a complex expression that causes the rule to fire, if the trigger code does not suffice.
 * @prop {string} state                 - the state of the rule defined at the user group level-- Enabled, Disabled or Silent.
 * @prop {string} uState                - the state of the rule defined at the individual user level.
 * @prop {boolean} modified             - indicates whether or not the rule has been
 * @prop {Action} action                - the action object defined for the rule.
 * @prop {string} customerTenants       - the comma delimited list of customer tenants
 */
export interface Rule {
  _id: string;
  triggerName: string;
  action: Action;
  physicianTitle?: string;
  physicianMessage?: string;
  group_id?: string;
  org_name?: string;
  tenant_name?: string;
  org_id?: string;
  contentPackage_id?: string;
  contentPackageRule_id?: string;
  rule_id?: string;
  triggerPriority?: string;
  renderEnvironment?: any;
  description?: string;
  triggerCategory?: string;
  referenceCategory?: string;
  triggerCode?: string;
  sqlExpression?: string;
  modified?: boolean;
  triggerInfoModified?: boolean;
  state?: string;
  uState?: string;
  docLevel?: boolean;
  customerTenants?: CustomerTenants;
  delayHours?: number;
  orgRuleId?: any;
  groupRuleId?: any;
  triggerSilent?: boolean;
  groupNames?: string[];
  lastUpdatedBy?: string;
  orgId?: string;
  groupId?: string;
  groupName?: string;
  triggerModified?: boolean;
  orgGroupRuleId?: OrgGroupRuleId;
  actionable?: boolean;
  actionableList?: string[];
}

export interface OrgGroupRuleId {
  triggerCode: string;
  orgId: string;
  groupId: string;
}

/**
 * Data object for the specific customer tenants related to CAPD rules
 *
 * @prop {string} name: customer name
 * @prop {string[]} tenants: cds tenants
 * **/
export interface CustomerTenants {
  name: string;
  tenants: string[];
}


/**
 * Data object that contains a relational collection of rule categories, and the rules within.
 *
 * @typedef {Object} RuleCategory
 * @param {string} triggerCategory - the name of the category.
 * @param {string} selText         - a customizable toggle string, used in the package creation component.
 * @param {Rule[]} members         - a list of rules in a category.
 */
export class RuleCategory {
  constructor(public triggerCategory: string, public members: Rule[], public selText: string) { }
}

/**
 * @param {string} _id                - the unique identifier of the package.
 * @param {string} contentPackage_id  - ??? who knows, who cares @todo
 * @param {string} name               - the name of the content package.
 * @param {string} version            - the version of the package.
 * @param {string} state              - whether the package is published or unpublished.
 * @param {string} requiredNlpVersion - the minimum NLP version the package needs to function.
 * @param {string} releaseDate        - the finalized publish date of the package.
 * @param {string} upgradeVersion     - if there is a new version of the package, its version is listed here.
 * @param {number} numberOfCustomers  - the number of customers using the package.
 * @param {number} numberOfRules      - the number of rules in the package.
 * @param {boolean} modified          - whether or not any of the rules in the package have been modified at
 * @param {Rule[]} newRules           - @todo I don't remember where this is used, either
 */
export interface Package {
  id: string;
  name: string;
  version: string;
  state: string;
  requiredNlpVersion?: string;
  numberOfCustomers?: number;
  numberOfRules?: number;
  releaseDate?: string;
  contentPackage_id?: string;
  modified?: boolean;
  upgradeVersion?: string;
  newRules?: Rule[];
  packageRules? : Rule[];
}

export interface DefaultPackage {
  _id: string;
  name: string;
  version: string;
  state: string;
  requiredNlpVersion?: string;
  numberOfCustomers?: number;
  numberOfRules?: number;
  releaseDate?: string;
  contentPackage_id?: string;
  modified?: boolean;
  upgradeVersion?: string;
  newRules?: Rule[];
  packageRules? : Rule[];
}

/** A package name with a list of its versions as an array.
 *
 * @typedef {Object} PackageName
 * @param {string} name       - The name of the package.
 * @param {Package[]} members - all versions of the package.
 * @param {boolean} seeAll    - a toggle to determine whether to show all versions of the package,
 *                              or just the three most recent versions.
 */
export class PackageName {
  seeAll = false;

  constructor(public name: string, public members: Package[]) { }

  public packDisplay() {
    if (this.seeAll) { return this.members; }
    else { return this.members.slice(0, 3); }
  }
}

/**
 * Interface for modeling Org settings.
 * @typedef {Object} OrgSettings
 * ***************************************
 * @todo the following properties are currently unused:
 * @prop {string} pipelineId                 - Specify the Hulk pipeline to use.
 * @prop {string} hccUrlBase                 - Specify the HCC URL Base to use. e.g., host
 * @prop {string} hccEndpoint                - Specify the HCC endpoint to use.
 * @prop {boolean} hccEnabled                - Toggle HCC messages for the customer.
 * @prop {boolean} showMessageBalloon        - Toggle the CAPD message balloons.
 * @prop {boolean} engageAnalyticsEnabled    - Toggle whether users have Engage Analytics Persisted for them or not
 * @prop {boolean} capdForFfiEnabled         - Toggle whether users use FFI and App Handler's that nest CDA within narrative for Engage
 * @prop {number} maxCacheDocumentPerContext - Maximum number of documents to store in cache.
 * **************************************
 * The following properties do have an effect:
 * @prop {string} tenantMapping              - The CAPD tenant to use.
 * @prop {string} mmodalNotes                - Notes that the admin user has on that customer.  
                                               Only viewable internally.
   @prop {any} nlpSessionParameters          - A freeform list of key:value pairs.
   @prop {any} hulkSessionParameters         - A freeform list of key:value pairs.
   @prop {any} capdServicesSessionParameters - A freeform list of key:value pairs.
   @prop {any} nlpRequestParameters          - A freeform list of key:value pairs.
   @prop {any} hulkRequestParameters         - A freeform list of key:value pairs.
   @prop {any} capdServicesRequestParameters - A freeform list of key:value pairs.
   @prop {any} hccSettings                   - A freeform list of key:value pairs.
   @prop {any} cdiCollaborateSettings        - A freeform list of key:value pairs.

 */

export interface OrgSettings {
  pipelineId?: string;
  tenantMapping?: string;
  hccUrlBase?: string;
  hccEndpoint?: string;
  hccEnabled?: boolean;
  showMessageBalloon?: boolean;
  engageAnalyticsEnabled?: boolean;
  capdForFfiEnabled?: boolean;
  maxCacheDocumentPerContext?: number;
  mmodalNotes?: string;
  nlpSessionParameters?: object;
  hulkSessionParameters?: object;
  capdServicesSessionParameters?: object;
  nlpRequestParameters?: object;
  hulkRequestParameters?: object;
  capdServicesRequestParameters?: object;
  hccSettings?: object;
  cdiCollaborateSettings?: object;
  modified?: any;
}

/** Interface containing an org's ID and their sub-settings.
 * @typedef {Object} Org
 * @prop {string} _id         - the truncated OID (organization ID).
 * @prop {object} OrgSettings - the settings associated with an org.  See above.
 */
export interface Org {
  _id: string;
  settings: OrgSettings;
}

/** Interface containing a RuleSet returned for content packages.
 * @typedef {Object} RuleSet
 * @prop {Rule[]} addedRules         - the rules to add.
 * @prop {[Rule]} conflictingRules   - the conflicting rules.
 * @prop {Rule[]} removedRules       - the rules to remove.
 */
export interface RuleSet {
  addedRules: Rule[];
  conflictingRules: [{from: Rule, to: Rule}];
  removedRules: Rule[];
}

/** Interface for CDS system info
 * @typedef {Object} CDSSystem
 * @prop {string} systemType        - the system type
 * @prop {string} systemName       - the system name
 */
export class CDSSystem {
  cdsType: string;
  cdsName: string;
}

export class SearchAllSettingsDisplay {
  org: string;
  orgName: string;
  key: string;
  value: string;
}
